import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Experience,
  ExperiencesCollection,
} from '@freelancer/datastore/collections';
import { TimeUtils } from '@freelancer/time-utils';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { LocalizedDateFns } from '@freelancer/ui/localized-date-fns.service';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { maxLength, required } from '@freelancer/ui/validators';
import { isFormControl } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

enum ExperienceFormInput {
  TITLE = 'title',
  COMPANY = 'company',
  START_MONTH = 'start_month',
  START_YEAR = 'start_year',
  END_MONTH = 'end_month',
  END_YEAR = 'end_year',
  CURRENTLY_WORKING = 'currently_working',
  SUMMARY = 'summary',
}

@Component({
  selector: 'app-user-profile-experiences-edit',
  template: `
    <fl-grid>
      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Experience title label" [weight]="FontWeight.BOLD">
          Title
        </fl-text>
        <ng-container
          *ngIf="experienceForm.get(ExperienceFormInput.TITLE) as control"
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfileExperiencesEditTitleInput"
            [control]="control"
            placeholder="Enter your position or title"
            i18n-placeholder="Experience title placeholder"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Experience company label" [weight]="FontWeight.BOLD">
          Company
        </fl-text>
        <ng-container
          *ngIf="experienceForm.get(ExperienceFormInput.COMPANY) as control"
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfileExperiencesEditCompanyInput"
            [control]="control"
            placeholder="Enter company name"
            i18n-placeholder="Experience company placeholder"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Experience start date label" [weight]="FontWeight.BOLD">
          Start
        </fl-text>
        <fl-bit class="DateRow">
          <ng-container
            *ngIf="
              experienceForm.get(ExperienceFormInput.START_MONTH) as control
            "
          >
            <fl-select
              *ngIf="isFormControl(control)"
              class="DateItem"
              flTrackingLabel="UserProfileExperiencesEditStartMonthInput"
              i18n-placeholder="Experience start month placeholder"
              placeholder="Select month"
              [flMarginRight]="Margin.SMALL"
              [control]="control"
              [options]="monthsList$ | flAsync"
            ></fl-select>
          </ng-container>

          <ng-container
            *ngIf="
              experienceForm.get(ExperienceFormInput.START_YEAR) as control
            "
          >
            <fl-select
              *ngIf="isFormControl(control)"
              class="DateItem"
              flTrackingLabel="UserProfileExperiencesEditStartYearInput"
              i18n-placeholder="Experience start year placeholder"
              placeholder="Select year"
              [flMarginBottom]="Margin.SMALL"
              [control]="control"
              [options]="yearsList$ | flAsync"
            ></fl-select>
          </ng-container>
        </fl-bit>
        <ng-container
          *ngIf="
            experienceForm.get(ExperienceFormInput.CURRENTLY_WORKING) as control
          "
        >
          <fl-checkbox
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserPRofileExperiencesEditCurrentlyWorking"
            i18n-label="Currently working label"
            label="I'm currently working here"
            [control]="control"
          ></fl-checkbox>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Experience end date label" [weight]="FontWeight.BOLD">
          End
        </fl-text>
        <fl-bit class="DateRow">
          <ng-container
            *ngIf="experienceForm.get(ExperienceFormInput.END_MONTH) as control"
          >
            <fl-select
              *ngIf="isFormControl(control)"
              class="DateItem"
              flTrackingLabel="UserProfileExperiencesEditEndMonthInput"
              i18n-placeholder="Experience end month placeholder"
              placeholder="Select month"
              [flMarginRight]="Margin.SMALL"
              [control]="control"
              [disabled]="disableEndDateInput$ | flAsync"
              [options]="monthsList$ | flAsync"
            ></fl-select>
          </ng-container>

          <ng-container
            *ngIf="experienceForm.get(ExperienceFormInput.END_YEAR) as control"
          >
            <fl-select
              *ngIf="isFormControl(control)"
              class="DateItem"
              flTrackingLabel="UserProfileExperiencesEditEndYearInput"
              i18n-placeholder="Experience end year placeholder"
              placeholder="Select year"
              [control]="control"
              [disabled]="disableEndDateInput$ | flAsync"
              [options]="yearsList$ | flAsync"
            ></fl-select>
          </ng-container>
        </fl-bit>

        <fl-text
          *ngIf="showInvalidDateMessage$ | flAsync"
          i18n="Invalid date selection error"
          [color]="FontColor.ERROR"
          [size]="TextSize.XXSMALL"
        >
          The end date can't be earlier than the start date.
        </fl-text>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Summary label" [weight]="FontWeight.BOLD">
          Summary
        </fl-text>

        <ng-container>
          <ng-container
            *ngIf="experienceForm.get(ExperienceFormInput.SUMMARY) as control"
          >
            <fl-textarea
              *ngIf="isFormControl(control)"
              flTrackingLabel="UserProfileExperiencesEdiSummaryInput"
              i18n-placeholder="Experience summary placeholder"
              placeholder="Describe your work experience"
              [rows]="5"
              [control]="control"
            ></fl-textarea>
          </ng-container>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-banner-alert
          *ngIf="isError$ | flAsync"
          i18n="Error message for experience action"
          [type]="BannerAlertType.ERROR"
        >
          Oops, something went wrong when saving your experience item. Please
          try again.
        </fl-banner-alert>
      </fl-col>

      <fl-col
        *ngIf="needsDeleteConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to delete this experience?"
          flTrackingLabel="UserProfileExperienceEditDelete"
          i18n-body="Delete experience confirmation body"
          i18n-title="Delete experience confirmation title"
          title="Delete Experience"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDeleteExperience()"
          (confirm)="handleConfirmDeleteExperience()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col
        *ngIf="needsCancelConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to discard any changes?"
          flTrackingLabel="UserProfileExperienceEditDiscardChanges"
          i18n-body="Discard changes confirmation body"
          i18n-title="Discard changes confirmation title"
          title="Discard Changes"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDiscardChanges()"
          (confirm)="handleConfirmDiscardChanges()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col *ngIf="!(hideActionRow$ | flAsync)" [col]="12">
        <app-user-profile-editable-ui-action-row
          deleteLinkText="Delete Experience"
          flTrackingLabel="UserProfileExperiencesEdit"
          i18n-deleteLinkText="Delete Experience link label"
          [disabled]="disableSaveButton$ | flAsync"
          [isBusy]="isBusy$ | flAsync"
          [showDeleteButton]="!!(currentExperienceId$ | flAsync)"
          (cancel)="handleCancel()"
          (delete)="handleDeleteExperience()"
          (save)="handleSave()"
        >
        </app-user-profile-editable-ui-action-row>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./user-profile-experiences-edit.component.scss'],
})
export class UserProfileExperiencesEditComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ExperienceFormInput = ExperienceFormInput;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  isFormControl = isFormControl;
  Margin = Margin;

  @Input() experience$: Rx.Observable<Experience | undefined>;
  @Input() experiencesCollection: DatastoreCollection<ExperiencesCollection>;
  // Draft experiences are experiences not saved to the backend.
  // Do not update datastore collection with draft experiences. Return it with modified fields.
  @Input() isDraftExperience = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() updateDraftExperience = new EventEmitter<Experience>();
  @Output() newDraftExperience = new EventEmitter<Experience>();

  // If this is set the user is currently editing a experience and if it is
  // undefined the user is creating a new experience item.
  // If this is a negative number the user is editing a draft(unpushed) experience item.
  private _currentExperienceId$ = new Rx.BehaviorSubject<number | undefined>(
    undefined,
  );
  private _disableEndDateInput$ = new Rx.BehaviorSubject<boolean>(false);
  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  private _isValidDate$ = new Rx.Subject<boolean>();
  private _needsCancelConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);

  currentExperienceId$ = this._currentExperienceId$.asObservable();
  disableEndDateInput$ = this._disableEndDateInput$.asObservable();
  isBusy$ = this._isBusy$.asObservable();
  isError$ = this._isError$.asObservable();
  isValidDate$ = this._isValidDate$.asObservable();
  needsCancelConfirmation$ = this._needsCancelConfirmation$.asObservable();
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  showInvalidDateMessage$: Rx.Observable<boolean>;
  disableSaveButton$: Rx.Observable<boolean>;
  hideActionRow$: Rx.Observable<boolean>;
  yearsList$: Rx.Observable<readonly (SelectItem | string)[]>;
  monthsList$: Rx.Observable<readonly (SelectItem | string)[]>;
  currentlyWorkingControlSubscription?: Rx.Subscription;
  experienceSubscription?: Rx.Subscription;
  validatorSubscription?: Rx.Subscription;

  titleControl = new FormControl('', [
    required($localize`Please enter your title.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  companyControl = new FormControl('', [
    required($localize`Please enter a company name.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  currentlyWorkingControl = new FormControl('');
  startMonthControl = new FormControl('');
  endMonthControl = new FormControl('');
  startYearControl = new FormControl('');
  endYearControl = new FormControl('');
  summaryControl = new FormControl('', [
    required($localize`Please describe your work experience.`),
    maxLength(500, $localize`Please enter at most 500 characters.`),
  ]);

  experienceForm = new FormGroup({
    [ExperienceFormInput.TITLE]: this.titleControl,
    [ExperienceFormInput.COMPANY]: this.companyControl,
    [ExperienceFormInput.START_MONTH]: this.startMonthControl,
    [ExperienceFormInput.START_YEAR]: this.startYearControl,
    [ExperienceFormInput.END_MONTH]: this.endMonthControl,
    [ExperienceFormInput.END_YEAR]: this.endYearControl,
    [ExperienceFormInput.CURRENTLY_WORKING]: this.currentlyWorkingControl,
    [ExperienceFormInput.SUMMARY]: this.summaryControl,
  });

  constructor(private date: LocalizedDateFns, private timeUtils: TimeUtils) {}

  ngOnInit(): void {
    this.setDateValidators();

    this.hideActionRow$ = Rx.combineLatest([
      this._needsDeleteConfirmation$,
      this._needsCancelConfirmation$,
    ]).pipe(
      map(([deleteConfirm, cancelConfirm]) => deleteConfirm || cancelConfirm),
    );

    this.showInvalidDateMessage$ = this.isValidDate$.pipe(map(valid => !valid));

    this.disableSaveButton$ = Rx.combineLatest([
      this.experienceForm.valueChanges.pipe(startWith(undefined)),
      this.isValidDate$.pipe(startWith(true)),
    ]).pipe(
      map(
        ([_, validDate]) =>
          !validDate ||
          !this.experienceForm.valid ||
          !this.experienceForm.dirty,
      ),
    );

    this.yearsList$ = this.timeUtils
      .getListOfYears(1900, new Date().getFullYear() + 1)
      .pipe(
        map(years =>
          years
            .map(year => ({ displayText: year.toString(), value: year }))
            .reverse(),
        ),
        // This is needed as to show the placeholder text.
        map(years => ['', ...years]),
      );

    const months$ = this.date.getLocalisedMonths();

    this.monthsList$ = months$.pipe(
      map(months =>
        months.map(month => ({
          displayText: month,
          value: months.indexOf(month) + 1,
        })),
      ),
      // This is needed as to show the placeholder text.
      map(months => ['', ...months]),
    );

    this.currentlyWorkingControlSubscription = this.currentlyWorkingControl.valueChanges.subscribe(
      isControlChecked => {
        this._disableEndDateInput$.next(isControlChecked);

        if (isControlChecked) {
          // Reset to '' as this is the value that shows placeholder text
          this.endMonthControl.reset('');
          this.endYearControl.reset('');

          this.endMonthControl.disable();
          this.endYearControl.disable();

          this._isValidDate$.next(true);
        } else {
          this.endMonthControl.enable();
          this.endYearControl.enable();
        }
      },
    );

    this.experienceSubscription = this.experience$.subscribe(experience => {
      // If the experience is undefined we are are creating a new one.
      if (!experience) {
        this._currentExperienceId$.next(undefined);
        return;
      }

      this._currentExperienceId$.next(experience.id);

      if (experience.startDate) {
        const startDate = new Date(experience.startDate);
        this.startMonthControl.setValue(startDate.getMonth() + 1);
        this.startYearControl.setValue(startDate.getFullYear());
      }

      if (experience.endDate) {
        const endDate = new Date(experience.endDate);
        this.endMonthControl.setValue(endDate.getMonth() + 1);
        this.endYearControl.setValue(endDate.getFullYear());
      } else {
        this.currentlyWorkingControl.setValue(true);
        this._disableEndDateInput$.next(true);
        this.endMonthControl.disable();
        this.endYearControl.disable();
      }

      this.summaryControl.setValue(experience.description);
      this.titleControl.setValue(experience.title);
      this.companyControl.setValue(experience.company);
    });

    this.validatorSubscription = Rx.combineLatest([
      this.startMonthControl.valueChanges.pipe(startWith(undefined)),
      this.startYearControl.valueChanges.pipe(startWith(undefined)),
      this.endMonthControl.valueChanges.pipe(startWith(undefined)),
      this.endYearControl.valueChanges.pipe(startWith(undefined)),
    ]).subscribe(() => {
      this.setDateValidators();
    });
  }

  handleSave(): void {
    const experienceId = this._currentExperienceId$.getValue();
    // If we have a experience id the experience already exists so we need to
    // update it instead of create a new one.
    if (experienceId) {
      this.updateExperience(experienceId);
    } else {
      this.createNewExperience();
    }
  }

  setDateValidators(): void {
    // Setup standard required validators.
    this.startYearControl.setValidators([
      required('Please select a start year'),
    ]);
    this.startMonthControl.setValidators([
      required('Please select a start month'),
    ]);

    this.endMonthControl.setValidators([
      required('Please select an end month'),
    ]);
    this.endYearControl.setValidators([required('Please select an end year')]);

    // Ensure that the start date is before the end date.
    const startYear = this.startYearControl.value;
    const startMonth = this.startMonthControl.value;

    const endYear = this.endYearControl.value;
    const endMonth = this.endMonthControl.value;

    // All dates are not given so we can't work out if the start date is earlier
    // then the end date.
    if (!startYear || !startMonth || !endYear || !endYear) {
      return;
    }

    const startDate = Date.UTC(startYear, startMonth);
    const endDate = Date.UTC(endYear, endMonth);

    if (startDate > endDate) {
      this._isValidDate$.next(false);
      return;
    }

    this._isValidDate$.next(true);
  }

  createNewExperience(): void {
    this._isBusy$.next(true);

    // TODO: T250105
    const startDate = Date.UTC(
      this.startYearControl.value,
      this.isDraftExperience
        ? this.startMonthControl.value - 1
        : this.startMonthControl.value,
    );
    const endDate = this._disableEndDateInput$.getValue()
      ? undefined
      : Date.UTC(
          this.endYearControl.value,
          this.isDraftExperience
            ? this.endMonthControl.value - 1
            : this.endMonthControl.value,
        );

    const newExperience: Experience = {
      userId: -1, // Needs the userId set. Not actually used in request.
      id: -Date.now(),
      company: this.companyControl.value,
      description: this.summaryControl.value,
      title: this.titleControl.value,
      startDate,
      endDate,
      // duration is for displaying draft experience only
      duration: startDate && endDate ? endDate - startDate : undefined,
      ordering: 0, // Unused in the request.
    };

    if (this.isDraftExperience) {
      this.newDraftExperience.emit(newExperience);
      this._isBusy$.next(false);
      this.cancel.emit();
    } else {
      this.experiencesCollection.push(newExperience).then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
    }
  }

  updateExperience(experienceId: number): void {
    this._isBusy$.next(true);

    // TODO: T250105
    const startDate = Date.UTC(
      this.startYearControl.value,
      this.isDraftExperience
        ? this.startMonthControl.value - 1
        : this.startMonthControl.value,
    );
    const endDate = this._disableEndDateInput$.getValue()
      ? undefined
      : Date.UTC(
          this.endYearControl.value,
          this.isDraftExperience
            ? this.endMonthControl.value - 1
            : this.endMonthControl.value,
        );

    const experienceFields = {
      company: this.companyControl.value,
      description: this.summaryControl.value,
      title: this.titleControl.value,
      startDate,
      endDate,
    };

    if (this.isDraftExperience) {
      const experience: Experience = {
        ...experienceFields,
        // duration is for displaying draft experience only
        duration: startDate && endDate ? endDate - startDate : undefined,
        id: experienceId,
        userId: -1, // Needs the userId set. Not actually used in request.
        ordering: 0, // Unused in the request.
      };

      this._isBusy$.next(false);
      this.updateDraftExperience.emit(experience);

      // Cancel out of the edit view on success.
      this.cancel.emit();
    } else {
      this.experiencesCollection
        .update(experienceId, experienceFields)
        .then(response => {
          this._isBusy$.next(false);

          if (response.status === 'error') {
            this._isError$.next(true);
            return;
          }

          // Cancel out of the edit view on success.
          this.cancel.emit();
        });
    }
  }

  handleConfirmDeleteExperience(): void {
    const experienceId = this._currentExperienceId$.getValue();
    // If this is not defined we should not be able to delete the experience.
    if (!experienceId) {
      return;
    }

    this._isBusy$.next(true);

    this.experiencesCollection.remove(experienceId).then(response => {
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);

      if (response.status === 'error') {
        this._isError$.next(true);
      }

      // Cancel out of the edit view on success.
      this.cancel.emit();
    });
  }

  handleCancelDeleteExperience(): void {
    this._needsDeleteConfirmation$.next(false);
  }

  handleCancelDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
  }

  handleConfirmDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
    this.cancel.emit();
  }

  handleCancel(): void {
    if (this.experienceForm.dirty) {
      this._needsCancelConfirmation$.next(true);
      return;
    }
    this.cancel.emit();
  }

  handleDeleteExperience(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  ngOnDestroy(): void {
    if (this.currentlyWorkingControlSubscription) {
      this.currentlyWorkingControlSubscription.unsubscribe();
    }

    if (this.experienceSubscription) {
      this.experienceSubscription.unsubscribe();
    }

    if (this.validatorSubscription) {
      this.validatorSubscription.unsubscribe();
    }
  }
}
